import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { useDialog } from "@/context/DialogContext";
import { absUrl } from "@/lib/config";
import {
  FloppyDisk, ArrowClockwise, UploadSimple, Trash, Buildings, ArrowRight, Eye, EyeSlash,
} from "@phosphor-icons/react";

const PRESET_COLORS = ["#002FA7", "#FF3B30", "#7C3AED", "#0A0A0A", "#F59E0B", "#10B981", "#EC4899", "#0EA5E9"];

function TextField({ label, value, onChange, hint, testid, multiline, rows = 3, placeholder }) {
  return (
    <div>
      <label className="eyebrow block mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full border border-border px-3 py-2 text-sm"
          placeholder={placeholder}
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-border px-3 py-2 text-sm"
          placeholder={placeholder}
          data-testid={testid}
        />
      )}
      {hint && <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function ColorPicker({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {PRESET_COLORS.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-8 h-8 border-2 ${value === c ? "border-[#0A0A0A]" : "border-transparent"}`}
          style={{ backgroundColor: c }}
          title={c}
          data-testid={`${testid}-${c}`}
        />
      ))}
      <input type="color" value={value || "#002FA7"} onChange={(e) => onChange(e.target.value)} className="w-10 h-8 border border-border" />
      <span className="text-[10px] font-mono-mini text-muted-foreground">{value}</span>
    </div>
  );
}

function ImageUploader({ label, value, onChange, hint, testid }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);

  const pick = () => ref.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (err) {
      setError(formatAxiosError(err));
    } finally { setUploading(false); }
  };

  const preview = value ? (value.startsWith("http") ? value : absUrl(value)) : "";

  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <div className="flex items-start gap-3">
        <div className="w-40 h-24 border border-border bg-muted flex items-center justify-center overflow-hidden">
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] font-mono-mini text-muted-foreground">No image</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={pick}
              disabled={uploading}
              className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-50"
              data-testid={`${testid}-upload`}
            >
              <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-[#FF3B30]"
                data-testid={`${testid}-clear`}
              >
                <Trash size={14} weight="bold" /> Clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste a URL"
            className="w-full border border-border px-2 py-1 text-xs font-mono-mini"
            data-testid={`${testid}-url`}
          />
          {hint && <div className="text-[10px] font-mono-mini text-muted-foreground">{hint}</div>}
          {error && <div className="text-xs text-[#FF3B30]">{error}</div>}
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" />
    </div>
  );
}

// ============ LIVE PREVIEW (mini mock of the login screen) ============
function LoginPreview({ b }) {
  const logo = b.logo_url ? (b.logo_url.startsWith("http") ? b.logo_url : absUrl(b.logo_url)) : "";
  const hero = b.right_image_url ? (b.right_image_url.startsWith("http") ? b.right_image_url : absUrl(b.right_image_url)) : "";
  return (
    <div className="border border-border overflow-hidden" data-testid="branding-preview">
      <div className={`grid ${b.show_right_panel ? "grid-cols-2" : "grid-cols-1"} bg-white`} style={{ minHeight: 340 }}>
        {/* Left panel */}
        <div className="flex flex-col justify-between p-4">
          <div className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt="" className="w-6 h-6 object-cover" />
            ) : (
              <div className="w-6 h-6 flex items-center justify-center" style={{ backgroundColor: b.brand_mark_color }}>
                <Buildings size={12} weight="bold" color="white" />
              </div>
            )}
            <div>
              <div className="font-display text-xs font-black leading-none">{b.brand_name}</div>
              <div className="text-[8px] uppercase tracking-widest mt-0.5 text-muted-foreground">{b.brand_tagline}</div>
            </div>
          </div>
          <div>
            <div className="text-[8px] uppercase tracking-widest text-muted-foreground mb-1">{b.eyebrow}</div>
            <h2 className="font-display text-lg font-black tracking-tight leading-[1.05] whitespace-pre-line">{b.headline}</h2>
            <p className="text-[10px] text-muted-foreground mt-1 max-w-[280px]">{b.subcopy}</p>
            <div className="mt-3 space-y-1.5">
              <div className="h-6 bg-muted border border-border" />
              <div className="h-6 bg-muted border border-border" />
              <div
                className="h-7 flex items-center justify-center gap-1 text-white text-[10px] uppercase tracking-widest"
                style={{ backgroundColor: b.brand_mark_color }}
              >
                {b.submit_label || "Sign in"} <ArrowRight size={10} weight="bold" />
              </div>
            </div>
          </div>
          <div className="text-[8px] text-muted-foreground">{b.footer_text}</div>
        </div>
        {/* Right panel */}
        {b.show_right_panel && (
          <div className="relative overflow-hidden border-l border-border">
            {hero && <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />}
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(45deg, ${b.right_overlay_color}80 0%, transparent 50%, #00000050 100%)` }}
            />
            <div className="absolute bottom-0 left-0 p-3 text-white">
              <div className="text-[8px] uppercase tracking-widest text-white/80 mb-0.5">{b.right_eyebrow}</div>
              <div className="font-display text-sm font-black leading-tight max-w-[220px]">{b.right_headline}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ MAIN ============
export default function BrandingTab() {
  const { branding, refresh } = useBranding();
  const { confirm } = useDialog();
  const [draft, setDraft] = useState(branding);
  const [initial, setInitial] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => { setDraft(branding); setInitial(branding); }, [branding]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);

  const set = (k) => (v) => setDraft(prev => ({ ...prev, [k]: v }));

  const flash = (type, text, ms = 3500) => {
    setMsg({ type, text });
    if (ms) setTimeout(() => setMsg({ type: "", text: "" }), ms);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/branding/login", draft);
      await refresh();
      flash("ok", "Branding saved. Log out to see the login page in production.");
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    } finally { setSaving(false); }
  };

  const revert = () => setDraft(initial);

  const resetToDefaults = async () => {
    const ok = await confirm({
      title: "Reset branding to defaults?",
      description: "This clears every override you have configured — logo, colors, typography, and login imagery.",
      confirmText: "Reset branding",
      variant: "destructive",
    });
    if (!ok) return;
    setResetting(true);
    try {
      await api.post("/branding/login/reset");
      await refresh();
      flash("ok", "Branding reset to defaults.");
    } catch (e) {
      flash("error", formatAxiosError(e), 6000);
    } finally { setResetting(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Customize what employees see on the login screen and the top-left brand mark inside the app.
            Changes take effect immediately after saving — no redeploy required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetToDefaults}
            disabled={resetting}
            className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-50"
            data-testid="branding-reset-defaults"
            title="Clear every override and use the built-in defaults"
          >
            <ArrowClockwise size={14} weight="bold" /> {resetting ? "Resetting…" : "Reset defaults"}
          </button>
          {dirty && (
            <button onClick={revert} className="border border-border px-3 py-2 text-sm hover:bg-muted">
              Undo changes
            </button>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
            data-testid="branding-save"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {msg.text && (
        <div className={`mb-4 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-[#22C55E] bg-green-50 text-[#166534]" : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"}`}>
          {msg.text}
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-8">
        {/* -------- Editor -------- */}
        <div className="space-y-8">
          <section>
            <div className="eyebrow mb-3">Brand identity</div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Brand name" value={draft.brand_name} onChange={set("brand_name")} testid="branding-brand-name" placeholder="ACME" />
                <TextField label="Tagline" value={draft.brand_tagline} onChange={set("brand_tagline")} testid="branding-tagline" placeholder="HR × PM Suite" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Brand mark color</label>
                <ColorPicker value={draft.brand_mark_color} onChange={set("brand_mark_color")} testid="branding-mark-color" />
                <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">Used for the small square icon, the primary CTA button, and the login gradient.</div>
              </div>
              <ImageUploader
                label="Logo image (optional)"
                value={draft.logo_url}
                onChange={set("logo_url")}
                hint="If set, replaces the brand-mark square. Square shape recommended (e.g. 128×128)."
                testid="branding-logo"
              />
            </div>
          </section>

          <section>
            <div className="eyebrow mb-3">Left panel copy</div>
            <div className="space-y-4">
              <TextField label="Eyebrow" value={draft.eyebrow} onChange={set("eyebrow")} testid="branding-eyebrow" placeholder="01 / Access" />
              <TextField
                label="Headline"
                value={draft.headline}
                onChange={set("headline")}
                multiline
                rows={2}
                hint="Use a line break (Enter) to force wrapping."
                testid="branding-headline"
              />
              <TextField label="Sub-copy" value={draft.subcopy} onChange={set("subcopy")} multiline rows={3} testid="branding-subcopy" />
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Submit button label" value={draft.submit_label} onChange={set("submit_label")} testid="branding-submit-label" placeholder="Sign in" />
                <TextField label="Footer text" value={draft.footer_text} onChange={set("footer_text")} testid="branding-footer" placeholder="© 2026 Company" />
              </div>
            </div>
          </section>

          <section>
            <div className="eyebrow mb-3 flex items-center justify-between">
              Right marketing panel
              <button
                onClick={() => set("show_right_panel")(!draft.show_right_panel)}
                className="text-[10px] font-mono-mini uppercase flex items-center gap-1 hover:text-[#002FA7]"
                data-testid="branding-toggle-right-panel"
              >
                {draft.show_right_panel ? <><Eye size={12} weight="bold" /> Visible</> : <><EyeSlash size={12} weight="bold" /> Hidden</>}
              </button>
            </div>
            {!draft.show_right_panel && (
              <div className="text-xs text-muted-foreground italic">Right panel is hidden — the form panel will span the entire login screen on desktop.</div>
            )}
            {draft.show_right_panel && (
              <div className="space-y-4">
                <ImageUploader
                  label="Hero image"
                  value={draft.right_image_url}
                  onChange={set("right_image_url")}
                  hint="Landscape orientation recommended (e.g. 1600×1200)."
                  testid="branding-hero"
                />
                <TextField label="Eyebrow (over image)" value={draft.right_eyebrow} onChange={set("right_eyebrow")} testid="branding-right-eyebrow" />
                <TextField label="Headline (over image)" value={draft.right_headline} onChange={set("right_headline")} multiline rows={2} testid="branding-right-headline" />
                <div>
                  <label className="eyebrow block mb-2">Overlay color</label>
                  <ColorPicker value={draft.right_overlay_color} onChange={set("right_overlay_color")} testid="branding-overlay-color" />
                  <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">Blends over the hero image to keep text legible.</div>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* -------- Live preview (sticky on wide screens) -------- */}
        <div>
          <div className="sticky top-4">
            <div className="eyebrow mb-3">Live preview</div>
            <LoginPreview b={draft} />
            <div className="text-[10px] font-mono-mini text-muted-foreground mt-2">
              Preview reflects unsaved edits. The real login page updates immediately after you save.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
